import { FormEvent, useEffect, useId, useMemo, useRef, useState } from "react"
import { Loader2Icon, LogInIcon, RotateCwIcon } from "lucide-react"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/shadcn/alert"
import { Button } from "@/components/ui/shadcn/button"
import { Checkbox } from "@/components/ui/shadcn/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/shadcn/dialog"
import { Field, FieldContent, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/shadcn/field"
import { Input } from "@/components/ui/shadcn/input"
import appStore from "@/stores/appStore"
import type { AuthResponse } from "@/types/common"
import { checkAuthPhone, requestAuthCode, verifyAuthCode, type AuthPhoneStatus } from "./authService"

type AuthDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  resendCooldownSeconds?: number
  onAuthSuccess?: (data: AuthResponse) => void | Promise<void>
  onRegistrationSuccess?: () => void
}

type AuthStep = "phone" | "code"
type PhoneStatus = "idle" | "submitting" | "error"
type CodeStatus = "idle" | "submitting" | "resending" | "error"
type UserKind = "existing" | "new" | "unknown"

const DEFAULT_RESEND_COOLDOWN_SECONDS = 180
const PHONE_EMPTY_VALUE = "+7 ("
const legalLinkClassName = "box-decoration-clone border-b border-current pb-px text-primary hover:text-primary-hover"

const AuthDialog = ({
  open,
  onOpenChange,
  resendCooldownSeconds = DEFAULT_RESEND_COOLDOWN_SECONDS,
  onAuthSuccess = (data) => appStore.onAuthCompleted(data),
  onRegistrationSuccess,
}: AuthDialogProps) => {
  const phoneInputId = useId()
  const codeInputId = useId()
  const termsConsentInputId = useId()
  const messagesConsentInputId = useId()
  const phoneInputRef = useRef<HTMLInputElement>(null)
  const codeInputRef = useRef<HTMLInputElement>(null)
  const operationGeneration = useRef(0)

  const [step, setStep] = useState<AuthStep>("phone")
  const [phoneStatus, setPhoneStatus] = useState<PhoneStatus>("idle")
  const [codeStatus, setCodeStatus] = useState<CodeStatus>("idle")
  const [phone, setPhone] = useState("")
  const [code, setCode] = useState("")
  const [userKind, setUserKind] = useState<UserKind>("unknown")
  const [phoneConsentStatus, setPhoneConsentStatus] = useState<AuthPhoneStatus>({
    exists: null,
    termsAccepted: null,
    messagesAccepted: null,
  })
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [messagesAccepted, setMessagesAccepted] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const [error, setError] = useState("")
  const [phoneValidationAttempted, setPhoneValidationAttempted] = useState(false)

  const phoneDigits = useMemo(() => getPhoneDigits(phone), [phone])
  const isPhoneValid = phoneDigits.length === 11 && phoneDigits.startsWith("7")
  const isCodeValid = /^\d{4}$/.test(code)
  const canSubmitPhone = phoneStatus !== "submitting"
  const needsTermsConsent = phoneConsentStatus.termsAccepted !== true
  const needsMessagesConsent = phoneConsentStatus.messagesAccepted !== true
  const effectiveTermsAccepted = phoneConsentStatus.termsAccepted === true || termsAccepted
  const effectiveMessagesAccepted = phoneConsentStatus.messagesAccepted === true || messagesAccepted
  const canSubmitCode = isCodeValid && effectiveTermsAccepted && effectiveMessagesAccepted && codeStatus !== "submitting"
  const submitCodeLabel = userKind === "new" ? "Зарегистрироваться и войти" : "Войти"

  useEffect(() => {
    if (!open) {
      operationGeneration.current += 1
      resetState()
      return
    }

    window.setTimeout(() => phoneInputRef.current?.focus(), 0)
  }, [open])

  useEffect(() => {
    if (!open || step !== "code" || cooldown <= 0) return

    const timer = window.setTimeout(() => {
      setCooldown((current) => Math.max(0, current - 1))
    }, 1000)

    return () => window.clearTimeout(timer)
  }, [cooldown, open, step])

  useEffect(() => {
    if (!open || step !== "code") return

    window.setTimeout(() => codeInputRef.current?.focus(), 0)
  }, [open, step])

  const resetState = () => {
    setStep("phone")
    setPhoneStatus("idle")
    setCodeStatus("idle")
    setPhone("")
    setCode("")
    setUserKind("unknown")
    setPhoneConsentStatus({ exists: null, termsAccepted: null, messagesAccepted: null })
    setTermsAccepted(false)
    setMessagesAccepted(false)
    setCooldown(0)
    setError("")
    setPhoneValidationAttempted(false)
  }

  const startCooldown = () => {
    setCooldown(Math.max(0, resendCooldownSeconds))
  }

  const handlePhoneChange = (value: string) => {
    setPhone(formatRussianPhone(value))
    setError("")
    setPhoneValidationAttempted(false)
  }

  const handleCodeChange = (value: string) => {
    setCode(value.replace(/\D/g, "").slice(0, 4))
    setError("")
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) operationGeneration.current += 1
    onOpenChange(nextOpen)
  }

  const submitPhone = async () => {
    if (phoneStatus === "submitting") return

    if (!isPhoneValid) {
      setPhoneValidationAttempted(true)
      setPhoneStatus("error")
      setError("")
      phoneInputRef.current?.focus()
      return
    }

    const generation = operationGeneration.current + 1
    operationGeneration.current = generation
    setPhoneStatus("submitting")
    setError("")

    let phoneStatusResult: AuthPhoneStatus = { exists: null, termsAccepted: null, messagesAccepted: null }

    try {
      try {
        phoneStatusResult = await checkAuthPhone(phone)
        if (generation !== operationGeneration.current) return
      } catch {
        if (generation !== operationGeneration.current) return
      }

      await requestAuthCode(phone)
      if (generation !== operationGeneration.current) return
      setUserKind(phoneStatusResult.exists === true ? "existing" : phoneStatusResult.exists === false ? "new" : "unknown")
      setPhoneConsentStatus(phoneStatusResult)
      setStep("code")
      setCode("")
      setTermsAccepted(false)
      setMessagesAccepted(false)
      setCodeStatus("idle")
      startCooldown()
      setPhoneStatus("idle")
    } catch {
      if (generation !== operationGeneration.current) return
      setPhoneStatus("error")
      setError("Не удалось отправить код. Проверьте номер и попробуйте ещё раз.")
    }
  }

  const submitCode = async () => {
    if (!canSubmitCode) return

    const generation = operationGeneration.current
    const isNewRegistration = userKind === "new"
    setCodeStatus("submitting")
    setError("")

    try {
      const authData = await verifyAuthCode({
        phone,
        code,
        termsAccepted: effectiveTermsAccepted,
        messagesAccepted: effectiveMessagesAccepted,
      })
      if (generation !== operationGeneration.current) return

      await onAuthSuccess(authData)
      if (generation !== operationGeneration.current) return
      await appStore.getUser()
      if (generation !== operationGeneration.current) return
      if (isNewRegistration && !authData.user?.email) onRegistrationSuccess?.()
      onOpenChange(false)
    } catch {
      if (generation !== operationGeneration.current) return
      setCodeStatus("error")
      setError("Код не подошёл или истёк. Проверьте код и попробуйте ещё раз.")
    }
  }

  const resendCode = async () => {
    if (cooldown > 0 || codeStatus === "resending") return

    const generation = operationGeneration.current
    setCodeStatus("resending")
    setError("")

    try {
      await requestAuthCode(phone)
      if (generation !== operationGeneration.current) return
      startCooldown()
      setCodeStatus("idle")
    } catch {
      if (generation !== operationGeneration.current) return
      setCodeStatus("error")
      setError("Не удалось запросить код повторно. Попробуйте ещё раз чуть позже.")
    }
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (step === "phone") {
      void submitPhone()
      return
    }

    void submitCode()
  }

  const phoneError = phoneValidationAttempted && !isPhoneValid ? "Введите российский номер полностью." : ""
  const codeError = code && !isCodeValid ? "Код состоит из 4 цифр." : ""

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Авторизация</DialogTitle>
          <DialogDescription>
            {step === "phone" ? "Введите номер телефона, чтобы получить сервисный код" : "Введите код из сообщения"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Не получилось</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          {step === "phone" ? (
            <FieldGroup>
              <Field data-invalid={Boolean(phoneError)}>
                <FieldLabel htmlFor={phoneInputId}>Номер телефона</FieldLabel>
                <Input
                  ref={phoneInputRef}
                  id={phoneInputId}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="+7 (___) ___-__-__"
                  value={phone}
                  aria-invalid={Boolean(phoneError)}
                  aria-describedby={phoneError ? `${phoneInputId}-error` : undefined}
                  disabled={phoneStatus === "submitting"}
                  onChange={(event) => handlePhoneChange(event.target.value)}
                />
                <FieldError id={`${phoneInputId}-error`}>{phoneError}</FieldError>
              </Field>
            </FieldGroup>
          ) : (
            <FieldGroup>
              <Field data-invalid={Boolean(codeError)}>
                <FieldLabel htmlFor={codeInputId}>Код подтверждения</FieldLabel>
                <Input
                  ref={codeInputRef}
                  id={codeInputId}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="0000"
                  value={code}
                  aria-invalid={Boolean(codeError)}
                  aria-describedby={codeError ? `${codeInputId}-error` : undefined}
                  disabled={codeStatus === "submitting"}
                  onChange={(event) => handleCodeChange(event.target.value)}
                />
                <FieldError id={`${codeInputId}-error`}>{codeError}</FieldError>
              </Field>

              <div className="-mt-2 flex min-h-6 items-center justify-start">
                {cooldown > 0 ? (
                  <FieldDescription className="text-xs">Запросить повторно через {cooldown} сек</FieldDescription>
                ) : (
                  <Button
                    type="button"
                    variant="link"
                    size="xs"
                    className="h-auto gap-1.5 rounded-none px-0 py-0 text-sm leading-normal hover:no-underline"
                    disabled={codeStatus === "resending"}
                    onClick={resendCode}
                  >
                    {codeStatus === "resending" ? <Loader2Icon data-icon="inline-start" className="animate-spin" /> : <RotateCwIcon data-icon="inline-start" />}
                    Запросить повторно
                  </Button>
                )}
              </div>

              {needsTermsConsent ? (
                <Field orientation="horizontal" data-invalid={!termsAccepted && codeStatus === "error"}>
                  <Checkbox
                    id={termsConsentInputId}
                    checked={termsAccepted}
                    aria-invalid={!termsAccepted && codeStatus === "error"}
                    disabled={codeStatus === "submitting"}
                    onCheckedChange={setTermsAccepted}
                  />
                  <FieldContent>
                    <FieldLabel htmlFor={termsConsentInputId} className="block text-sm font-normal leading-normal text-muted-foreground">
                      Соглашаюсь с{" "}
                      <a href="/about/terms" target="_blank" rel="noreferrer" className={legalLinkClassName}>
                        условиями использования
                      </a>{" "}
                      и{" "}
                      <a href="/about/privacy" target="_blank" rel="noreferrer" className={legalLinkClassName}>
                        политикой конфиденциальности
                      </a>
                    </FieldLabel>
                  </FieldContent>
                </Field>
              ) : null}
              {needsMessagesConsent ? (
                <Field orientation="horizontal" data-invalid={!messagesAccepted && codeStatus === "error"}>
                  <Checkbox
                    id={messagesConsentInputId}
                    checked={messagesAccepted}
                    aria-invalid={!messagesAccepted && codeStatus === "error"}
                    disabled={codeStatus === "submitting"}
                    onCheckedChange={setMessagesAccepted}
                  />
                  <FieldContent>
                    <FieldLabel htmlFor={messagesConsentInputId} className="block text-sm font-normal leading-normal text-muted-foreground">
                      Соглашаюсь получать сервисные сообщения по заказам и авторизации
                    </FieldLabel>
                    {userKind === "unknown" ? (
                      <FieldDescription>
                        Не удалось заранее определить статус пользователя, поэтому согласие запрашивается до входа
                      </FieldDescription>
                    ) : null}
                  </FieldContent>
                </Field>
              ) : null}
            </FieldGroup>
          )}

          <DialogFooter className="gap-3">
            {step === "phone" ? (
              <div className="flex w-full flex-col gap-3">
                <Button type="submit" size="lg" disabled={!canSubmitPhone} className="w-full">
                  {phoneStatus === "submitting" ? <Loader2Icon data-icon="inline-start" className="animate-spin" /> : <LogInIcon data-icon="inline-start" />}
                  Продолжить
                </Button>
                <p className="text-sm leading-normal text-muted-foreground">
                  Нажимая «Продолжить», вы соглашаетесь на обработку номера телефона для авторизации и получение сервисного кода. {" "}
                  <a href="/about/privacy" className={legalLinkClassName}>
                    Политика конфиденциальности
                  </a>
                  .
                </p>
              </div>
            ) : (
              <Button type="submit" size="lg" disabled={!canSubmitCode} className="w-full">
                {codeStatus === "submitting" ? <Loader2Icon data-icon="inline-start" className="animate-spin" /> : <LogInIcon data-icon="inline-start" />}
                {submitCodeLabel}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const getPhoneDigits = (value: string) => {
  const digits = value.replace(/\D/g, "")

  if (digits.startsWith("8")) {
    return `7${digits.slice(1, 11)}`
  }

  if (digits.startsWith("9")) {
    return `7${digits.slice(0, 10)}`
  }

  return digits.slice(0, 11)
}

const formatRussianPhone = (value: string) => {
  const digits = getPhoneDigits(value)

  if (!digits) return ""

  const normalizedDigits = digits.startsWith("7") ? digits : `7${digits.slice(0, 10)}`
  const area = normalizedDigits.slice(1, 4)
  const first = normalizedDigits.slice(4, 7)
  const second = normalizedDigits.slice(7, 9)
  const third = normalizedDigits.slice(9, 11)

  let formatted = PHONE_EMPTY_VALUE

  if (area) {
    formatted += area
  }

  if (area.length === 3) {
    formatted += ")"
  }

  if (first) {
    formatted += ` ${first}`
  }

  if (second) {
    formatted += `-${second}`
  }

  if (third) {
    formatted += `-${third}`
  }

  return formatted
}

export default AuthDialog
